import { Box } from "@mui/material";
import Link from "next/link";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import {
  SITE_COLOR,
  SITE_MEDIA,
  SITE_TYPE,
  SITE_VAR,
  SITE_WRAP,
} from "src/domains/shared/constants/styles/site";
import { manualTopicHref } from "../Manual/manualJump";
import { InlineSvg } from "../siteKit/InlineSvg";
import { siteFileUrl, type SiteFileKey } from "../siteKit/media/siteMedia";
import { replayOnEnterRef, revealRef } from "../siteKit/motion/reveal";
import { useFadeOutOnLeave } from "../siteKit/motion/useFadeOutOnLeave";
import { SITE_LINK_ARROW_STYLES } from "../siteKit/siteButtonStyles";
import {
  riseOrder,
  SITE_HERO_COMPACT_MARK_RIGHT,
  SITE_HERO_COMPACT_MARK_SIZE,
  SITE_HERO_LANDSCAPE_MARK_SIZE,
  SITE_HERO_LEAD_GAP,
  SITE_HERO_MARK_SIZE,
  SITE_HERO_RISE_STYLES,
  SITE_HERO_TEXT_TOP,
  SITE_HERO_TOP,
  siteHeroTextTop,
  siteHeroTileShadow,
} from "../siteKit/siteHeroStyles";
import { groupReleasesByMonth, RELEASE_ITEMS } from "./releaseItems";
import { SiteTitle } from "../siteKit/SiteText";

type ReleaseEntry = (typeof RELEASE_ITEMS)[number];

/** 가로 터치 화면(`SITE_MEDIA.landscapeTouch`)의 그림 칸 폭 — 소식 칸의 44% 이하, 16:10 그림이 머리말·여백을 뺀 화면 높이에 들어오게 */
const LANDSCAPE_ART_WIDTH = "min(44%, calc((100svh - 140px) * 1.6))";
/** 오른쪽에 둔 「시연 영상 보기」 가 칸 끝에 붙지 않게 띄우는 여유 */
const DEMO_RIGHT_INSET = "16px";

/** 터치 화면: 시연 링크의 누르는 영역을 소식 칸 전체로(링크 `::after` 가 `position: relative` 인 소식 칸을 덮는다) */
const COVER_ARTICLE = {
  "&::after": { content: '""', position: "absolute", inset: 0 },
} as const;

const RELEASES_STYLES = {
  top: {
    paddingTop: SITE_HERO_TOP,
    position: "relative",
    isolation: "isolate",
    minHeight: `calc(${SITE_HERO_TOP} + ${SITE_HERO_MARK_SIZE} + 40px)`,
    [SITE_MEDIA.tablet]: { minHeight: 0 },
    [SITE_MEDIA.landscapeTouch]: { minHeight: 0 },
  },
  // 첫 화면 오른쪽 앱 타일 자리 — 움직이지 않고 바닥 그림자를 진다(타일 높이에 따라 크기만 바뀌고, 가운데에서 타일 폭 4% 오른쪽). 타일은 안에서 떠 있다
  mark: {
    ...siteHeroTileShadow("4%"),
    position: "absolute",
    zIndex: -1,
    // 본문 오른쪽 끝에 맞춘다(타일이 기울어 생기는 빈 모서리만큼 8px 바깥으로)
    right: `max(calc(${SITE_VAR.gutter} - 8px),calc((100vw - 1240px) / 2))`,
    top: SITE_HERO_TOP,
    width: SITE_HERO_MARK_SIZE,
    aspectRatio: "1",
    pointerEvents: "none",
    // 휴대폰: 타일을 오른쪽 위에 — 타일을 28% 올리므로 그림자도 그만큼 올린다
    [SITE_MEDIA.tablet]: {
      width: SITE_HERO_COMPACT_MARK_SIZE,
      right: SITE_HERO_COMPACT_MARK_RIGHT,
      bottom: "auto",
      "&::before": { top: "75%" },
    },
    // 가로 터치 화면: 화면 높이에 맞춘 타일을 첫머리 글 오른쪽에(넓은 화면처럼 바닥 그림자는 타일 아래)
    [SITE_MEDIA.landscapeTouch]: {
      width: SITE_HERO_LANDSCAPE_MARK_SIZE,
      right: `calc(${SITE_VAR.gutter} - 8px)`,
      "&::before": { top: "103%" },
    },
  },
  // 공식 마크(노랑·남색 두 삼각형)를 얹은 살짝 기운 앱 아이콘 타일
  markTile: {
    position: "absolute",
    inset: 0,
    borderRadius: "26%",
    background: "linear-gradient(150deg,#F6F9FF 0%,#E4EDFF 55%,#D2E1FF 100%)",
    boxShadow:
      "0 34px 70px -24px rgba(29,116,255,.38),0 0 0 1px rgba(29,116,255,.08),inset 0 2px 0 #fff",
    transform: "rotate(-6deg)",
    animation: "releasesFloat 8s ease-in-out infinite alternate",
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      borderRadius: "inherit",
      background:
        "linear-gradient(180deg,rgba(255,255,255,.75),rgba(255,255,255,0) 60%)",
    },
    "& svg": {
      position: "absolute",
      left: "18%",
      top: "17%",
      width: "64%",
      height: "64%",
      display: "block",
      filter: "drop-shadow(0 12px 18px rgba(39,81,163,.22))",
    },
    "@keyframes releasesFloat": {
      from: { transform: "translateY(-4%) rotate(-7deg)" },
      to: { transform: "translateY(4%) rotate(-4deg)" },
    },
    [SITE_MEDIA.reducedMotion]: { animation: "none" },
    [SITE_MEDIA.tablet]: {
      transform: "translateY(-28%) rotate(-6deg)",
      animationName: "releasesFloatSmall",
      "@keyframes releasesFloatSmall": {
        from: { transform: "translateY(-32%) rotate(-7deg)" },
        to: { transform: "translateY(-24%) rotate(-4deg)" },
      },
    },
    [SITE_MEDIA.landscapeTouch]: {
      transform: "rotate(-6deg)",
      animationName: "releasesFloat",
    },
  },
  hero: {
    ...SITE_WRAP,
    ...SITE_HERO_RISE_STYLES,
    marginBottom: "clamp(48px,6.6vw,100px)",
    minHeight: SITE_HERO_MARK_SIZE,
    [SITE_MEDIA.tablet]: { minHeight: 0 },
    [SITE_MEDIA.landscapeTouch]: { minHeight: SITE_HERO_LANDSCAPE_MARK_SIZE },
  },
  // 제목은 매뉴얼 첫 화면과 같은 높이에서 시작한다
  heroText: {
    paddingTop: SITE_HERO_TEXT_TOP,
    paddingRight: "clamp(0px,30vw,380px)",
    [SITE_MEDIA.tablet]: { paddingTop: 0, paddingRight: 0 },
    [SITE_MEDIA.landscapeTouch]: {
      paddingTop: siteHeroTextTop(SITE_HERO_LANDSCAPE_MARK_SIZE),
      paddingRight: `calc(${SITE_HERO_LANDSCAPE_MARK_SIZE} + 24px)`,
    },
  },
  lead: {
    ...SITE_TYPE.lead,
    marginTop: SITE_HERO_LEAD_GAP,
    maxWidth: "none",
    textWrap: "balance",
  },
  list: { ...SITE_WRAP, paddingTop: "clamp(16px,3vw,40px)", paddingBottom: 0 },
  // 달 구역 — 넓은 화면은 왼쪽 레일에 달 제목, 오른쪽에 소식. 좁은 화면·가로 터치 화면은 달 제목이 소식 위 한 줄.
  // 첫 구역의 윗선이 첫머리와 목록을 가르고, 그 선과 첫 소식 사이도 다른 구역 사이와 같은 여백이다
  month: {
    display: "grid",
    gridTemplateColumns: "minmax(0,220px) minmax(0,1fr)",
    gap: "clamp(32px,5vw,96px)",
    paddingBlock: "clamp(48px,6vw,88px)",
    borderTop: `1px solid ${SITE_COLOR.line}`,
    "&:last-of-type": { paddingBottom: "clamp(40px,5vw,72px)" },
    [SITE_MEDIA.tablet]: { gridTemplateColumns: "1fr", gap: "20px", paddingBlock: "44px" },
    [SITE_MEDIA.landscapeTouch]: { gridTemplateColumns: "1fr", gap: "20px", paddingBlock: "44px" },
  },
  // 달 제목은 소식보다 눈에 덜 띄는 작은 회색 글자 — 목록은 「언제」가 아니라 「무엇이 바뀌었나」를 알린다. 넓은 화면에서는 구역 안에서 따라온다
  monthLabel: {
    position: "sticky",
    top: "88px",
    alignSelf: "start",
    paddingTop: "6px",
    fontSize: "15px",
    fontWeight: 600,
    color: SITE_COLOR.ink3,
    letterSpacing: "-.005em",
    fontVariantNumeric: "tabular-nums",
    [SITE_MEDIA.tablet]: { position: "static", paddingTop: 0 },
    [SITE_MEDIA.landscapeTouch]: { position: "static", paddingTop: 0 },
  },
  // 소식 — 넓은 화면: 글(제목·설명·시연) 왼쪽을 그림 높이 가운데에, 그림 오른쪽. 좁은 화면: 제목 → 그림 → 설명 → 시연 한 열(글 묶음을 풀어 이 격자에 놓는다).
  // 가로 터치 화면: 그림 왼쪽(화면 높이 안), 글 오른쪽
  item: {
    display: "grid",
    gridTemplateColumns: "minmax(0,1fr) minmax(0,420px)",
    gridTemplateAreas: '"text art"',
    columnGap: "clamp(28px,4vw,60px)",
    alignItems: "center",
    paddingBlock: "clamp(32px,3.8vw,52px)",
    borderTop: `1px solid ${SITE_COLOR.line2}`,
    "&:first-of-type": { borderTop: 0, paddingTop: 0 },
    [SITE_MEDIA.tablet]: {
      position: "relative",
      gridTemplateColumns: "minmax(0,1fr)",
      gridTemplateAreas: '"title" "art" "desc" "demo"',
      alignItems: "start",
      rowGap: "14px",
      paddingBlock: "36px",
    },
    [SITE_MEDIA.landscapeTouch]: {
      position: "relative",
      gridTemplateColumns: `minmax(0,${LANDSCAPE_ART_WIDTH}) minmax(0,1fr)`,
      gridTemplateAreas: '"art title" "art desc" "art demo"',
      gridTemplateRows: "auto auto 1fr",
      alignItems: "start",
      columnGap: "clamp(24px,4vw,48px)",
      rowGap: "10px",
      paddingBlock: "28px",
    },
  },
  text: {
    gridArea: "text",
    [SITE_MEDIA.tablet]: { display: "contents" },
    [SITE_MEDIA.landscapeTouch]: { display: "contents" },
  },
  // 기능 이름이 목록에서 가장 큰 글자다 — 넓은 화면은 글 묶음이 그림과 무게가 맞게 크게
  title: {
    fontSize: "clamp(24px,2.4vw,34px)",
    fontWeight: 700,
    letterSpacing: "-.03em",
    lineHeight: 1.3,
    textWrap: "balance",
    [SITE_MEDIA.tablet]: { gridArea: "title", fontSize: "24px" },
    [SITE_MEDIA.landscapeTouch]: { gridArea: "title", fontSize: "24px" },
  },
  description: {
    marginTop: "16px",
    fontSize: "clamp(16px,1.3vw,18px)",
    lineHeight: 1.75,
    color: SITE_COLOR.ink2,
    maxWidth: "30em",
    [SITE_MEDIA.tablet]: { gridArea: "desc", marginTop: 0, fontSize: "16px" },
    [SITE_MEDIA.landscapeTouch]: { gridArea: "desc", marginTop: 0, fontSize: "16px" },
  },
  // 「시연 영상 보기」 자리 — 넓은 화면·가로 터치 화면은 글 아래 왼쪽, 좁은 화면은 오른쪽(오른손 엄지가 닿는 쪽) 끝에서 조금 안으로
  demoRow: {
    marginTop: "28px",
    [SITE_MEDIA.tablet]: {
      gridArea: "demo",
      marginTop: "2px",
      display: "flex",
      justifyContent: "flex-end",
      paddingRight: DEMO_RIGHT_INSET,
    },
    [SITE_MEDIA.landscapeTouch]: {
      gridArea: "demo",
      alignSelf: "start",
      marginTop: "4px",
      display: "block",
      paddingRight: 0,
    },
  },
  demo: {
    ...SITE_LINK_ARROW_STYLES,
    fontSize: "16px",
    [SITE_MEDIA.tablet]: { ...COVER_ARTICLE, fontSize: "15px" },
    [SITE_MEDIA.landscapeTouch]: { ...COVER_ARTICLE, fontSize: "15px" },
  },
  art: {
    gridArea: "art",
    margin: 0,
    aspectRatio: "16/10",
    borderRadius: "24px",
    overflow: "hidden",
    position: "relative",
    background: "#F3F6FA",
    "& svg": { width: "100%", height: "100%", display: "block" },
    [SITE_MEDIA.landscapeTouch]: { alignSelf: "start" },
  },
  // 화면 밖 그림은 스타일·레이아웃 계산을 건너뛴다 — 그림 19장의 SVG 요소가 문서의 94%(약 7천 개)라, 회전처럼 문서 전체를
  // 다시 계산할 때 화면 밖 그림까지 매번 계산하던 것을 화면 안 그림으로 줄인다. 칸 크기는 figure 의 16:10 이 정해 건너뛰는 동안에도 자리가 같다
  artHost: { width: "100%", height: "100%", contentVisibility: "auto" },
} as const;

const ARROW_PATH = "M6 3l5 5-5 5";

const parseIsoDate = (date: string) => new Date(`${date}T00:00:00Z`);

/**
 * 시연 절이 있는 소식의 「시연 영상 보기」 — 설명 아래. 그 기능이 절 중간에 나오면 그 단계부터 열리는 주소(`?step=N`)로 간다.
 * 시연 절이 없으면 아무것도 그리지 않는다
 */
function ReleaseDemoLink({ item }: { item: ReleaseEntry }) {
  const { t } = useTranslation("landing");
  if (!("demo" in item)) return null;
  const topic = t(`site.manual.topics.${item.demo}.title`);
  const step = "demoStep" in item ? item.demoStep : undefined;
  return (
    <Box sx={RELEASES_STYLES.demoRow}>
      <Box
        component={Link}
        href={manualTopicHref(item.demo, step)}
        aria-label={
          step
            ? t("site.releases.demoStepLabel", { topic, step })
            : t("site.releases.demoLabel", { topic })
        }
        sx={RELEASES_STYLES.demo}
      >
        {t("site.releases.demo")}
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
          <path
            d={ARROW_PATH}
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Box>
    </Box>
  );
}

/**
 * 업데이트 소식(`/releases`, releases): 첫머리와 달별 소식 목록. 기능 이름과 그림이 먼저 눈에 띄고,
 * 시기는 달 제목만 말한다(소식마다 날짜를 적지 않는다). 열면 최신 소식을 본 것으로 기억한다.
 */
export function ReleasesPage() {
  const { t, i18n } = useTranslation("landing");
  const heroRef = useFadeOutOnLeave<HTMLDivElement>();
  const titleId = useId();
  const months = groupReleasesByMonth(RELEASE_ITEMS);
  const monthFormat = new Intl.DateTimeFormat(i18n.language, {
    year: "numeric",
    month: "long",
    timeZone: "UTC",
  });

  return (
    <>
      <Box
        component="section"
        sx={RELEASES_STYLES.top}
        aria-labelledby={titleId}
      >
        <Box aria-hidden="true" sx={RELEASES_STYLES.mark}>
          <Box sx={RELEASES_STYLES.markTile}>
            <svg viewBox="2 2 28 29">
              <path
                fill="#F0C66D"
                d="M25.3198 3.86572L3.97754 12.0342L22.5569 26.6094L25.3198 3.86572Z"
              />
              <path
                fill="#2751A3"
                d="M28.0226 10.9556L10.9248 17.4846L25.8092 29.1345L28.0226 10.9556Z"
              />
            </svg>
          </Box>
        </Box>
        <Box
          ref={(element: HTMLDivElement | null) => {
            heroRef.current = element;
            return replayOnEnterRef(element);
          }}
          sx={RELEASES_STYLES.hero}
        >
          <Box sx={RELEASES_STYLES.heroText}>
            <Box component="p" sx={SITE_TYPE.eyebrow}>
              {t("site.releases.eyebrow")}
            </Box>
            <Box component="h1" id={titleId} sx={SITE_TYPE.h1}>
              <SiteTitle text={t("site.releases.title")} />
            </Box>
            <Box component="p" style={riseOrder(1)} sx={RELEASES_STYLES.lead}>
              {t("site.releases.lead")}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={RELEASES_STYLES.list}>
        {months.map(({ month, items }) => {
          const monthId = `m-${month}`;
          return (
            <Box
              component="section"
              key={month}
              sx={RELEASES_STYLES.month}
              aria-labelledby={monthId}
            >
              <Box component="h2" id={monthId} sx={RELEASES_STYLES.monthLabel}>
                {monthFormat.format(parseIsoDate(`${month}-01`))}
              </Box>
              <div>
                {items.map((item) => {
                  const title = t(`site.releases.items.${item.key}.title`);
                  return (
                    <Box
                      component="article"
                      key={item.key}
                      ref={revealRef}
                      data-reveal="up"
                      sx={RELEASES_STYLES.item}
                    >
                      <Box sx={RELEASES_STYLES.text}>
                        <Box component="h3" sx={RELEASES_STYLES.title}>
                          <SiteTitle text={title} />
                        </Box>
                        <Box component="p" sx={RELEASES_STYLES.description}>
                          {t(`site.releases.items.${item.key}.description`)}
                        </Box>
                        <ReleaseDemoLink item={item} />
                      </Box>
                      <Box
                        component="figure"
                        role="img"
                        aria-label={t("site.releases.artLabel", { title })}
                        sx={RELEASES_STYLES.art}
                      >
                        <InlineSvg
                          url={siteFileUrl(
                            `release-art/${item.key}.svg` as SiteFileKey
                          )}
                          sx={RELEASES_STYLES.artHost}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </div>
            </Box>
          );
        })}
      </Box>
    </>
  );
}
